/**
 * Parses Server-Sent Events from a fetch() response body (POST streaming).
 * Return true from onEvent to stop reading; caller should abort the fetch if needed.
 */
export async function consumeSseStream(
  body: ReadableStream<Uint8Array>,
  onEvent: (eventName: string, data: string) => boolean | void
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let currentEvent = 'message';
  const dataLines: string[] = [];

  const dispatch = () => {
    const data = dataLines.join('\n');
    dataLines.length = 0;
    const ev = currentEvent;
    currentEvent = 'message';
    if (onEvent(ev, data)) {
      reader.cancel().catch(() => {});
    }
  };

  const pushLine = (line: string) => {
    if (line === '') {
      dispatch();
      return;
    }
    if (line.startsWith('event:')) {
      currentEvent = line.slice(6).trimStart();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trimStart());
    }
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        buffer += decoder.decode();
        if (buffer.length) {
          for (const line of buffer.split(/\r?\n/)) {
            pushLine(line);
          }
        }
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      while (true) {
        const nl = buffer.indexOf('\n');
        if (nl === -1) break;
        let line = buffer.slice(0, nl);
        buffer = buffer.slice(nl + 1);
        if (line.endsWith('\r')) line = line.slice(0, -1);
        pushLine(line);
      }
    }
  } finally {
    reader.releaseLock();
  }
}
